import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LoadingService } from '../../service/loading-service.service';
import { SnackbarService } from '../snackbar/snackbar.service';

export interface InvoiceFormData {
  organizationNumber: string;
  contactPerson: string;
  invoiceEmail: string;
  eInvoiceEnabled?: boolean;
  invoiceTiming?: 'immediate' | 'next-month';
}

export interface PaymentRequest {
  invoiceData?: InvoiceFormData;
  paymentMethod: 'invoice' | 'card';
  bookingData?: any;
}

export interface PaymentResponse {
  success?: boolean;
  message?: string;
  checkoutUrl?: string;
  checkout_url?: string; // Stripe API response format
  checkout_session_id?: string; // Stripe checkout session ID
  invoiceId?: string;
  invoice_id?: number;
  assignment_id?: number;
  amount?: number;
  currency?: string;
  shifts_count?: number;
  customer?: any;
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private readonly http = inject(HttpClient);
  private readonly loadingService = inject(LoadingService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly apiUrl = environment.apiUrl;

  //Service for invoice payment
  submitAssignmentInvoice(invoiceData: any): Observable<PaymentResponse> {
    this.loadingService.showLoader();
    // Pass the entire payload as-is to preserve all fields
    const payload = {
      orgNumber: invoiceData.orgNumber,
      assignmentId: invoiceData.assignmentId,
      contactPerson: invoiceData.contactPerson,
      email: invoiceData.email,
      eInvoiceEnabled: invoiceData.eInvoiceEnabled,
      billingStatus: invoiceData.billingStatus,
      eInvoice: invoiceData.eInvoice,
      amount: invoiceData.amount,
      currency: invoiceData.currency
    };

    return this.http.post<PaymentResponse>(`${this.apiUrl}/payments/invoice`, payload).pipe(
      catchError(error => {
        console.error('Invoice payment submission failed', error);
        this.snackbarService.showError(error.error?.error || 'Failed to create invoice. Please try again.');
        return throwError(() => error);
      }),
      finalize(() => {
        this.loadingService.hideLoader();
      })
    );
  }

  createCheckoutSession(payload: Record<string, any>): Observable<PaymentResponse> {
    return this.http
      .post<PaymentResponse>(`${this.apiUrl}/payments/create-checkout-session`, payload)
      .pipe(
        catchError((error) => {
          console.error('Checkout session API error:', error);
          return throwError(() => new Error(error.message || 'Something went wrong!'));
        })
      );
  }

  /**
   * Verify payment session after Stripe redirect
   * @param sessionId The Stripe checkout session ID
   * @returns Observable with verification result
   */
  verifyPaymentSession(sessionId: string): Observable<PaymentResponse> {
    return this.http
      .get<PaymentResponse>(`${this.apiUrl}/payments/verify`, {
        params: { session_id: sessionId }
      })
      .pipe(
        catchError((error) => {
          console.error('Payment verification API error:', error);
          this.snackbarService.showError('Failed to verify payment. Please try again.');
          return throwError(() => new Error(error.message || 'Payment verification failed'));
        })
      );
  }
}

